<script setup>
import { onMounted, ref } from "vue"

const props = defineProps({
  customCountries: {
    type: Array,
    default: () => [],
  },
  countryId: {
    type: String,
    default: "",
  },
  error: {
    type: String,
    default: "",
  },
})

const emit = defineEmits(["countrySelected", "error"])

const selectedCountry = ref("")
const selectedCountryObj = ref(null)
const isDropdownOpen = ref(false)

function setDefaultCountry() {
  if (props.countryId && props.customCountries) {
    const country = props.customCountries.find(country => country.id === props.countryId)
    if (country) {
      selectedCountry.value = country.code
      selectedCountryObj.value = country
    }
  }
}

onMounted(() => {
  setDefaultCountry()
})

function getFlagEmoji(countryCode) {
  if (!countryCode)
    return ""

  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map(char => 127397 + char.charCodeAt(0))

  return String.fromCodePoint(...codePoints)
}

function handleCountryChange(country) {
  selectedCountry.value = country.code
  selectedCountryObj.value = country
  emit("countrySelected", country.id)
  isDropdownOpen.value = false // Close dropdown after selection
}
function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value
}
</script>

<template>
  <div class="country-select-container">
    <label for="country-select" class="country-label">
      Select Country
    </label>

    <div class="select-wrapper">
      <div
        class="custom-dropdown"
        :aria-expanded="isDropdownOpen.toString()"
        tabindex="0"
        @click="toggleDropdown"
      >
        <span class="selected-country">
          <!-- Display flag and country name -->
          <span v-if="selectedCountryObj">
            <span class="flag-icon">{{ getFlagEmoji(selectedCountryObj.code) }}</span>
            {{ selectedCountryObj.name }} ({{ selectedCountryObj.code }})
          </span>
          <span v-else>
            Select a country
          </span>
        </span>
        <img data-v-ccd5a67e="" src="/assets/images/down-arrow.svg" alt="" style="width: 12px;">
      </div>

      <ul v-if="isDropdownOpen" class="dropdown-list">
        <li
          v-for="country in customCountries"
          :key="country.code"
          class="dropdown-item"
          @click="handleCountryChange(country)"
        >
          <!-- Display flag and country name in dropdown options -->
          <span class="flag-icon">{{ getFlagEmoji(country.code) }}</span>
          {{ country.name }} ({{ country.code }})
        </li>
      </ul>

      <small v-if="props.error" class="error">{{ props.error }}</small>
    </div>
  </div>
</template>

<style scoped>
/* Custom CSS styling */
.country-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.select-wrapper {
  position: relative;
}

.custom-dropdown {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--be-small-card-bg);
  color: var(--be-primary-text-color);
  padding: 0.5rem 0.7rem;
  font-size: 12px;
  height: 40px; /* Fixed height */
  cursor: pointer;
  border-radius: 2px;
  width: 100%;
  box-sizing: border-box;
}

.selected-country {
  flex-grow: 1;
  opacity: 0.5;
}
.arrow {
  font-size: 16px;
}
.flag-icon{
  margin-right: 4px;
}
.dropdown-list {
  position: absolute;
  top: 38px;
  left: 0;
  right: 0;
  background-color: var(--be-small-card-bg);
  border-radius: 4px;
  z-index: 9999;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 5px;
  padding: 0;
  list-style: none;
}

.dropdown-item {
  padding: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.1s ease;
}

.dropdown-item:hover {
background: var(--be-primary-btn);
  color:black
}

.error {
  color: #ff5252;
  margin-top: 3px;
  font-size: 11px;
}

.dropdown-list::-webkit-scrollbar {
  width: 4px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 4px;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background-color: #999;
  border-radius: 5px;
  border: 1px solid transparent;
  background-clip: content-box;
}
</style>
